import { Button, Intent, NonIdealState } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, showError, showSuccess, useGridState } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import styled from "styled-components";

import { AffixRequestApi, NewKennelRequestImportDto, Result } from "../../../../../api";
import { CustomDataTable, SaveButton } from "../../../../../components";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";

export interface IAffixImportPageProps {
  importFunction: (files: File[]) => Promise<NewKennelRequestImportDto[]>;
  saveFunction: (items: NewKennelRequestImportDto[]) => Promise<Result>;
  columns: IDataTableColumn[];
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const AffixImportPage: React.FunctionComponent<IAffixImportPageProps> = ({
  importFunction,
  saveFunction,
  columns,
}) => {
  const [files, setFiles] = React.useState<any[]>();
  const { t, tUnsafe } = useTl();
  const [importing, setimporting] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [uploading, setuploading] = React.useState(false);
  const api = useApiService(AffixRequestApi);

  const tableState = useGridState<any>({
    serverMode: false,
    availablePageSizes: [15, 25, 50],
    enablePagination: false,
    enableFilter: false,
  });

  const importCallback = React.useCallback(async () => {
    setimporting(true);
    try {
      const res = await importFunction(files);
      setimporting(false);
      showSuccess(t(ETLCodes.FileReadSuccessfully));
      tableState.setData(res);
      return res;
    } catch {
      showError(t(ETLCodes.Error));
      setimporting(false);
    }
  }, [files, importFunction, t, tableState]);

  const wordImportCallBack = React.useCallback(async () => {
    setuploading(true);
    try {
      console.log(files);
      const docs = files.filter((f) => f.fileExtension === "docx");
      await api.uploadFciDoc(docs.map((f) => ({ data: f.file, fileName: f.filename })));
      showSuccess(t(ETLCodes.ImportSucessfull));
    } catch (e) {
      if (e?.errorCode) {
        showError(tUnsafe(`BackendError_${e.errorCode}`));
      } else {
        showError(t(ETLCodes.Error));
      }
    }
    setuploading(false);
  }, [api, files, t, tUnsafe]);

  const saveImport = React.useCallback(async () => {
    setSaving(true);
    try {
      await saveFunction(tableState.data);
      setSaving(false);
      showSuccess(t(ETLCodes.SaveSuccess));
      tableState.setData([]);
      setFiles([]);
    } catch {
      showError(t(ETLCodes.Error));
      setSaving(false);
    }
  }, [saveFunction, t, tableState]);

  return (
    <>
      <FilePond
        files={files}
        allowMultiple={true}
        onupdatefiles={setFiles}
        labelIdle={t(ETLCodes.FilePondDragOrSelect)}
      />
      <ButtonContainer>
        <Button
          onClick={importCallback}
          loading={importing}
          text={t(ETLCodes.ReadExcelFile)}
          disabled={!files || files.length === 0}
          minimal={false}
          intent={Intent.PRIMARY}
          icon={<i className="far fa-file-excel" />}
        />
        <Button
          onClick={wordImportCallBack}
          loading={uploading}
          text={t(ETLCodes.UploadWordFile)}
          disabled={!files || files.length === 0}
          minimal={false}
          intent={Intent.PRIMARY}
          icon={<i className="far fa-file-word" />}
        />
      </ButtonContainer>

      {tableState?.data && files?.length > 0 ? (
        <FieldSet title={t(ETLCodes.Results)}>
          <CustomDataTable tableState={tableState} columns={columns} />
          <div style={{ marginTop: "0.5rem" }}>
            <SaveButton
              onClick={saveImport}
              loading={saving}
              disabled={!files || files.length === 0 || false}
              minimal={false}
              intent={Intent.PRIMARY}
            />
          </div>
        </FieldSet>
      ) : (
        <NonIdealState icon="resolve" description={t(ETLCodes.PleaseImportFile)} />
      )}
    </>
  );
};
